import React from "react";
import { Link } from "react-router-dom";
import { iso2viewTime, numericDate2view } from "../../utils/time";
import { intComma } from "humanize-plus";
import { ChargeableTripsRow } from "../../api";
import { Shape, SelectedTrip } from "../../interfaces";
import {
    selectTrip,
    setCanUseMapNeighbors,
} from "../../services/common/common";
import Box from "@mui/material/Box";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import MapIcon from "@mui/icons-material/Map";
import MenuItem from "@mui/material/MenuItem";
import { convertShiftDateToJpn, convertUTCtoJST } from "../../utils/formatting";
import {
    ResourceType,
    sendLog,
    StatusType,
    ActionType,
} from "../../services/common/send_log";
import { HeadCell } from "../../components/table/sortableTable";
import { colors, fontSizes } from "../../theme";

const rideType = {
    "0": "流し",
    "1": "GO",
};

enum NaviUsageFlag {
    YES = "あり",
    NO = "なし",
}

export const headCells: HeadCell[] = [
    {
        label: "乗務日",
        id: "shiftDate",
        numeric: false,
        format: (r: number): string => {
            return numericDate2view(r);
        },
        grow: 1,
    },
    {
        label: "営業所",
        id: "officeName",
        numeric: false,
        grow: 4,
    },
    {
        label: "乗務員",
        id: "driverName",
        numeric: false,
        grow: 3,
    },
    {
        label: "開始地点",
        id: "startLocation",
        numeric: false,
        grow: 3,
    },
    {
        label: "開始時刻",
        id: "unladenTripStartedAt",
        headerAlignRight: true,
        numeric: true,
        format: (r: string): string => {
            return iso2viewTime(r);
        },
        grow: 1,
    },
    {
        label: "実車地点",
        id: "deliveredLocation",
        numeric: false,
        grow: 3,
    },
    {
        label: "実車時刻",
        id: "deliverStartedAt",
        numeric: true,
        headerAlignRight: true,
        format: (r: string): string => {
            if (r) {
                return iso2viewTime(r);
            }
            return "";
        },
        grow: 1,
    },
    {
        label: "乗車種別",
        id: "rideType",
        numeric: false,
        grow: 1,
        format: (r: number): string => {
            return toRideType(r);
        },
    },
    {
        label: "実車時ナビ利用",
        id: "useNaviAtDelivered",
        numeric: false,
        headerAlignRight: false,
        grow: 1,
        format: (r: boolean): string => {
            return toNaviUsage(r);
        },
    },
    {
        label: "ナビ終了後走行時間（分）",
        id: "deliveredDurationAfterNaviOff",
        numeric: true,
        headerAlignRight: true,
        disablePadding: true,
        format: (r: number, row: Row): string => {
            if (row.useNaviAtDelivered === false) {
                return intComma(Math.floor(r / 60));
            }
            return "";
        },
        grow: 1,
    },
    {
        label: "地図",
        id: "row_id",
        numeric: false,
        cell: function UpdateMapElementCell(r: Row): any {
            return <LinkToMap selectedTrip={r} />;
        },
        grow: 2,
    },
];

export type Row = {
    deliveredDurationAfterNaviOff: number;
    useNaviAtDelivered: boolean;
    rideType: number;
    deliverStartedAt: string;
    unladenTripStartedAt: string;
    driverName: string;
    driverCode: string;
    shiftDate: number;
    naviShape: any;
    unladenShape: any;
    deliverStartLoc: any;
    unladenStartLoc: any;
    trip?: ChargeableTripsRow;
    tripSessionId: string;
    loginSessionId: string;
    boardingType: string;
    row_id: string;
};

const toRideType = (rt?: number): string => {
    if (rt == undefined) {
        return "";
    }
    return rideType[rt.toString()];
};

const toNaviUsage = (pk?: boolean): string => {
    if (pk == undefined) {
        return "";
    }
    if (pk) {
        return NaviUsageFlag.YES;
    }
    return NaviUsageFlag.NO;
};

const convertRecToTrip = (rec?: Row): SelectedTrip | null => {
    if (!rec) {
        return null;
    }

    const shiftDate = rec.deliverStartedAt
        ? convertShiftDateToJpn(rec.deliverStartedAt.toString().split("T")[0])
        : "";

    const unladenStart = rec.unladenTripStartedAt
        ? convertUTCtoJST(rec.unladenTripStartedAt)
        : "";

    return {
        tripSessionId: rec.tripSessionId || null,
        loginSessionId: rec.loginSessionId || null,
        shiftDate: shiftDate,
        driverCode: rec.driverCode || null,
        driverName: rec.driverName || null,
        unladenStartTs: unladenStart,
        unladenStartTsRaw: rec.unladenTripStartedAt || null,
        boardingType: rec.boardingType || null,
        boardingTs: convertUTCtoJST(rec.deliverStartedAt) || null,
        details: {
            deliverStartLoc: rec.deliverStartLoc as Shape,
            unladenStartLoc: rec.unladenStartLoc as Shape,
            naviShape: rec.naviShape as Shape,
            unladenShape: rec.unladenShape as Shape,
        },
    } as SelectedTrip;
};

interface MapLinkProps {
    selectedTrip: Row;
}

const LinkToMap: React.FC<MapLinkProps> = (props: MapLinkProps) => {
    let naviShapeExists = false;
    if (props.selectedTrip && props.selectedTrip.naviShape) {
        naviShapeExists = true;
    }

    let linkColor = colors.gray.main;
    let cursorType = "default";

    if (naviShapeExists) {
        linkColor = colors.green.maplink;
        cursorType = "pointer";
    }

    return (
        <Box style={{ cursor: cursorType }}>
            <MenuItem
                to="/trips_map"
                component={Link}
                sx={{ color: colors.blue.main, p: 0 }}
                onClick={(ev: any) => {
                    if (!naviShapeExists) {
                        ev.preventDefault();
                        setCanUseMapNeighbors(true);
                    }
                }}
            >
                <MapIcon
                    sx={{
                        color: linkColor,
                        fontSize: fontSizes.mapicon,
                        pt: 1,
                        pb: 1,
                    }}
                    onClick={async (ev: any) => {
                        if (!naviShapeExists) {
                            ev.preventDefault();
                            selectTrip(null);
                            return;
                        }

                        const _trip = convertRecToTrip(props.selectedTrip);
                        await selectTrip(_trip);

                        sendLog(
                            ResourceType.TRIPS_MAP,
                            StatusType.GET_RESOURCE,
                            ActionType.TRIPS_MAP_USAGE
                        );
                    }}
                />
            </MenuItem>
        </Box>
    );
};
